import React, { useState, useMemo } from 'react';
import { Paper, Popper, List, ListItem, ListItemText, Typography, Button, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Project } from '../../../../../types/project';
import { FormattedMessage, useIntl } from 'react-intl';
import HtmlParser from '../../../../../components/HtmlParser';

type ProjectSelectionDialogProps = {
	open: boolean;
	onClose: () => void;
	onSelect: (project: Project) => void;
	anchorEl: HTMLElement | null;
	projects: Project[];
};

const ProjectSelectionDialog: React.FC<ProjectSelectionDialogProps> = ({ open, onClose, onSelect, anchorEl, projects }) => {
	const { formatMessage } = useIntl();
	const [searchQuery, setSearchQuery] = useState('');

	const filteredProjects = useMemo(() => {
		if (!searchQuery.trim()) return projects;

		const query = searchQuery.toLowerCase();
		return projects.filter(
			project =>
				project.projectName.toLowerCase().includes(query) ||
				(project.projectSummary && project.projectSummary.toLowerCase().includes(query)),
		);
	}, [projects, searchQuery]);

	if (!open) return null;

	return (
		<>
			<Popper
				open={open}
				anchorEl={anchorEl}
				placement='top-start'
				style={{
					zIndex: 99999,
					width: anchorEl ? Math.max(anchorEl.getBoundingClientRect().width, 600) : '75rem',
					maxWidth: '90vw',
				}}
			>
				<Paper
					elevation={3}
					sx={{
						maxHeight: '80vh',
						display: 'flex',
						flexDirection: 'column',
						'& .MuiList-root': {
							flex: 1,
							overflow: 'auto',
							minHeight: 0,
						},
					}}
				>
					<TextField
						variant='standard'
						placeholder={formatMessage({ id: 'projectSelection.searchPlaceholder' })}
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						sx={{ p: 2, flexShrink: 0 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position='start'>
									<SearchIcon />
								</InputAdornment>
							),
						}}
					/>
					{filteredProjects.length === 0 ? (
						<Typography sx={{ p: 2, textAlign: 'center', flexShrink: 0 }}>
							<FormattedMessage id='projectSelection.noProjects' defaultMessage='No projects available' />
						</Typography>
					) : (
						<List>
							{filteredProjects.map(project => (
								<ListItem
									key={project.projectId}
									onClick={() => onSelect(project)}
									sx={{
										cursor: 'pointer',
										'&:hover': {
											backgroundColor: 'action.hover',
										},
									}}
								>
									<ListItemText
										primary={project.projectName}
										secondary={project.projectSummary ? HtmlParser(project.projectSummary) : ''}
									/>
								</ListItem>
							))}
						</List>
					)}
				</Paper>
			</Popper>
		</>
	);
};

export default ProjectSelectionDialog;
